/* eslint-disable class-methods-use-this */
import {
    DemeterFilterTimeSpan,
    DemeterSymbolContractModel,
    DemeterSymbolModel,
    ListMarketPricesWithConversionsResponse,
    SymbolCategory,
    UnitOfMeasure,
} from '../../../../Generated/Raven-Demeter';
import { IChartBarDataSeries, IChartDataSeries } from '../../../Components/Charts/ChartDefinitions';
import formattingService from '../../../Services/Formatting/FormattingService';
import {
    dairySpreadUnitOfMeasures,
    energySpreadUnitOfMeasures,
    ICalendarAverageAnchor,
    ICalendarSpreadCalculationResult,
    ICalendarSpreadEntriesByDate,
    ICalendarSpreadPeriodCalculation,
} from './CalendarSpreadDefinitions';

class CalendarSpreadCalculatorService {
    getCalculationGridData = (
        responses: ListMarketPricesWithConversionsResponse[],
        selectedContract1: DemeterSymbolContractModel,
        selectedContract2: DemeterSymbolContractModel,
    ) => {
        const todaysDate = new Date();
        const cutoffDate = this.getCutoffDate(DemeterFilterTimeSpan.OneYear, todaysDate);
        const formattedSelectedContract1 = this.getFormattedDataFromResponse(responses, [selectedContract1], true).filter(
            (x) => x.masterAsOfDate.getTime() >= cutoffDate.getTime(),
        );
        const formattedSelectedContract2 = this.getFormattedDataFromResponse(responses, [selectedContract2], false);
        const selectedContractsWithSpread = this.getLineSeriesForSpreadType(formattedSelectedContract1, formattedSelectedContract2).sort(
            (a, b) => a.masterAsOfDate.getTime() - b.masterAsOfDate.getTime(),
        );

        return this.buildSpreadPeriodCalculations(selectedContractsWithSpread);
    };

    getProjectionLineAndBarSeries = (
        responses: ListMarketPricesWithConversionsResponse[],
        selectedContract1: DemeterSymbolContractModel,
        selectedContract2: DemeterSymbolContractModel,
        timeSpan: DemeterFilterTimeSpan,
        labelContractOne: string,
        labelContractTwo: string,
        projectionBarLabel: string,
    ) => {
        const todaysDate = new Date();
        const cutoffDate = this.getCutoffDate(timeSpan, todaysDate);
        const formattedSelectedContract1 = this.getFormattedDataFromResponse(responses, [selectedContract1], true).filter(
            (x) => x.masterAsOfDate.getTime() >= cutoffDate.getTime(),
        );
        const formattedSelectedContract2 = this.getFormattedDataFromResponse(responses, [selectedContract2], false);
        const selectedContractsWithSpread = this.getLineSeriesForSpreadType(formattedSelectedContract1, formattedSelectedContract2).sort(
            (a, b) => a.masterAsOfDate.getTime() - b.masterAsOfDate.getTime(),
        );

        const contractOneLineSeries: IChartDataSeries = {
            label: labelContractOne,
            data: selectedContractsWithSpread.map((x) => ({
                value: x.contract1Price ?? 0,
                asOfDate: x.masterAsOfDate,
                isActualValue: true,
            })),
        };

        const contractTwoLineSeries: IChartDataSeries = {
            label: labelContractTwo,
            data: selectedContractsWithSpread.map((x) => ({
                value: x.contract2Price ?? 0,
                asOfDate: x.masterAsOfDate,
                isActualValue: true,
            })),
        };

        const spreadBarSeries: IChartBarDataSeries = {
            label: projectionBarLabel,
            data: selectedContractsWithSpread.map((x) => ({
                value: x.spread ?? 0,
                asOfDate: x.masterAsOfDate,
                isActualValue: true,
            })),
        };

        return {
            lineSeries: [contractOneLineSeries, contractTwoLineSeries],
            barSeries: spreadBarSeries,
        };
    };

    getthisYearAndLastYearSeaonalLineSeries = (
        responses: ListMarketPricesWithConversionsResponse[],
        selectedContract1: DemeterSymbolContractModel,
        selectedContract2: DemeterSymbolContractModel,
        lastYearContract1: DemeterSymbolContractModel,
        lastYearContract2: DemeterSymbolContractModel,
        previousYearsContract1: DemeterSymbolContractModel[],
        previousYearsContract2: DemeterSymbolContractModel[],
        thisYearLabel: string,
        lastYearLabel: string,
        averageLabel: string,
    ) => {
        const thisYearChartMinimum = new Date(new Date().setFullYear(selectedContract1.year - 1, selectedContract1.month, 1));
        const lastYearChartMinimum = new Date(new Date().setFullYear(lastYearContract1.year - 1, lastYearContract1.month, 1));
        const lastYearChartMaximum = new Date(new Date().setFullYear(lastYearContract1.year, lastYearContract1.month, 0));
        const lastYearDateAnchors: ICalendarAverageAnchor[] = [];
        const formattedSelectedContractValues1 = this.getFormattedDataFromResponse(responses, [selectedContract1], true).filter(
            (x) => x.masterAsOfDate.getTime() >= thisYearChartMinimum.getTime(),
        );
        const formattedSelectedContractValues2 = this.getFormattedDataFromResponse(responses, [selectedContract2], false);
        const formattedLastYearContractValues1 = this.getFormattedDataFromResponse(responses, [lastYearContract1], true).filter(
            (x) => x.masterAsOfDate.getTime() >= lastYearChartMinimum.getTime() && x.masterAsOfDate.getTime() <= lastYearChartMaximum.getTime(),
        );
        const formattedLastYearContractValues2 = this.getFormattedDataFromResponse(responses, [lastYearContract2], false);
        const selectedContractsValuesWithSpread = this.getLineSeriesForSpreadType(formattedSelectedContractValues1, formattedSelectedContractValues2).sort(
            (a, b) => a.masterAsOfDate.getTime() - b.masterAsOfDate.getTime(),
        );
        const lastYearContractsValuesWithSpread = this.getLineSeriesForSpreadType(formattedLastYearContractValues1, formattedLastYearContractValues2).sort(
            (a, b) => a.masterAsOfDate.getTime() - b.masterAsOfDate.getTime(),
        );
        const lastYearContractsClone = [...lastYearContractsValuesWithSpread];

        lastYearContractsValuesWithSpread.forEach((item) => {
            if (item.masterAsOfDate.getMonth() + 1 === 2 && item.masterAsOfDate.getDate() === 29) {
                return;
            }

            lastYearDateAnchors.push({
                masterAsOfDate: item.masterAsOfDate,
                contracts: [],
            });
        });

        const matchedPreviousYearsContracts = previousYearsContract1.map((contract, i) => ({
            previousContract1: contract,
            previousContract2: previousYearsContract2[i],
        }));

        const formattedPreviousYearsContractsValues = matchedPreviousYearsContracts.map(({ previousContract1, previousContract2 }) => {
            const previousContractMaximum = new Date(new Date().setFullYear(previousContract1.year, previousContract1.month, 0));
            const previousContractMinimum = new Date(new Date().setFullYear(previousContract1.year - 1, previousContract1.month, 1));

            return {
                formattedContract1: this.getFormattedDataFromResponse(responses, [previousContract1], true).filter(
                    (x) => x.masterAsOfDate.getTime() >= previousContractMinimum.getTime() && x.masterAsOfDate.getTime() <= previousContractMaximum.getTime(),
                ),
                formattedContract2: this.getFormattedDataFromResponse(responses, [previousContract2], false),
            };
        });

        const previousYearsContractsValuesWithSpread = formattedPreviousYearsContractsValues.map(({ formattedContract1, formattedContract2 }) => {
            const combinedValuesWithSpread = this.getLineSeriesForSpreadType(formattedContract1, formattedContract2).sort(
                (a, b) => a.masterAsOfDate.getTime() - b.masterAsOfDate.getTime(),
            );

            return combinedValuesWithSpread;
        });

        const groupedPreviousYearsContracts = lastYearContractsClone.reduce((previousYearsContracts: ICalendarAverageAnchor[], referenceContract, index) => {
            const item: ICalendarAverageAnchor = {
                masterAsOfDate: referenceContract.masterAsOfDate,
                contracts: [],
            };

            previousYearsContractsValuesWithSpread.forEach((sortedContracts) => {
                if (index > sortedContracts.length - 1) {
                    item.contracts.push(sortedContracts[sortedContracts.length - 1]);
                } else {
                    item.contracts.push(sortedContracts[index]);
                }
            });

            previousYearsContracts.push(item);
            return previousYearsContracts;
        }, []);

        const averagedContractsLineData = groupedPreviousYearsContracts.map((anchorWithContracts) => {
            const averaged =
                anchorWithContracts.contracts.reduce((sum, value) => {
                    if (!value?.spread) {
                        return sum;
                    }

                    return sum + value.spread;
                }, 0) / anchorWithContracts.contracts.length;
            const result: ICalendarSpreadEntriesByDate = {
                masterAsOfDate: anchorWithContracts.masterAsOfDate,

                spread: averaged,
                contract1AsOfDate: null,
                contract2AsOfDate: null,
                yearNumber: 0,
                contract1ReutersInstrumentCode: null,
                contract1Price: null,
                contract2ReutersInstrumentCode: null,
                contract2Price: null,
            };

            return result;
        });

        const thisYearSeries: IChartDataSeries = {
            label: thisYearLabel,
            data: selectedContractsValuesWithSpread.map((point) => ({
                value: point.spread ?? 0,
                asOfDate: new Date(new Date(point.masterAsOfDate).setFullYear(point.yearNumber - 1)),
                originalAsOfDate: point.masterAsOfDate,
                isActualValue: true,
            })),
        };

        const lastYearSeries: IChartDataSeries = {
            label: lastYearLabel,
            data: lastYearContractsValuesWithSpread.map((point) => ({
                value: point.spread ?? 0,
                asOfDate: point.masterAsOfDate,
                originalAsOfDate: point.masterAsOfDate,
                isActualValue: true,
            })),
        };

        const averagesSeries: IChartDataSeries = {
            label: averageLabel,
            data: averagedContractsLineData.map((point) => ({
                value: point.spread ?? 0,
                asOfDate: point.masterAsOfDate,
                originalAsOfDate: point.masterAsOfDate,
                isActualValue: true,
            })),
        };

        return [thisYearSeries, lastYearSeries, averagesSeries];
    };

    getFormattedDataFromResponse = (responses: ListMarketPricesWithConversionsResponse[], contracts: DemeterSymbolContractModel[], isBaseContract: boolean) => {
        const contractResponse: ListMarketPricesWithConversionsResponse[] = responses.filter((response) =>
            response.rows?.some((row) => contracts.some((contract) => contract.reutersInstrumentCode === row.reutersInstrumentCode)),
        );
        const contractMarketPrices = contractResponse.flatMap((response) => response.rows?.filter((row) => row.reutersInstrumentCode) || []);

        const formattedEntries = contractMarketPrices.map((marketPrice) => {
            const asOfDate = new Date(marketPrice.asOfDate);
            const yearNumber = asOfDate.getFullYear();

            return {
                masterAsOfDate: asOfDate,
                contract1AsOfDate: isBaseContract ? asOfDate : null,
                contract2AsOfDate: !isBaseContract ? asOfDate : null,
                yearNumber,
                contract1ReutersInstrumentCode: isBaseContract ? marketPrice.reutersInstrumentCode : null,
                contract1Price: isBaseContract ? marketPrice.settlementPrice : null,
                contract2ReutersInstrumentCode: !isBaseContract ? marketPrice.reutersInstrumentCode : null,
                contract2Price: !isBaseContract ? marketPrice.settlementPrice : null,
                spread: null,
            } as ICalendarSpreadEntriesByDate;
        });

        return formattedEntries;
    };

    getLineSeriesForSpreadType = (formattedEntries1: ICalendarSpreadEntriesByDate[], formattedEntries2: ICalendarSpreadEntriesByDate[]) => {
        const contract1Lookup = new Map<string, ICalendarSpreadEntriesByDate>();
        formattedEntries1.forEach((entry) => {
            const key = formattingService.toLongDayMonthYear(entry.contract1AsOfDate);
            if (key.length && !contract1Lookup.has(key)) {
                contract1Lookup.set(key, entry);
            }
        });

        const contract2Lookup = new Map<string, ICalendarSpreadEntriesByDate>();
        formattedEntries2.forEach((entry) => {
            const key = formattingService.toLongDayMonthYear(entry.contract2AsOfDate);
            if (key.length && !contract2Lookup.has(key)) {
                contract2Lookup.set(key, entry);
            }
        });

        const combinedEntriesMap = new Map<string, ICalendarSpreadEntriesByDate>();
        Array.from(contract1Lookup.entries()).forEach(([key, value]) => {
            const matchingEntry2 = contract2Lookup.get(key);
            if (matchingEntry2 && value.contract1AsOfDate && value.contract1Price !== null && matchingEntry2.contract2Price !== null) {
                combinedEntriesMap.set(key, {
                    masterAsOfDate: value.contract1AsOfDate,
                    contract1AsOfDate: value.contract1AsOfDate,
                    contract2AsOfDate: matchingEntry2?.contract2AsOfDate || null,
                    yearNumber: value.yearNumber,
                    contract1ReutersInstrumentCode: value.contract1ReutersInstrumentCode,
                    contract1Price: value.contract1Price,
                    contract2ReutersInstrumentCode: matchingEntry2?.contract2ReutersInstrumentCode || null,
                    contract2Price: matchingEntry2.contract2Price,
                    spread: value.contract1Price - matchingEntry2.contract2Price,
                });
            }
        });

        const combinedEntries = Array.from(combinedEntriesMap.values()).sort((a, b) => b.masterAsOfDate.getTime() - a.masterAsOfDate.getTime());
        return combinedEntries;
    };

    getUnitOfMeasure = (symbol1: DemeterSymbolModel, currentUnitOfMeasure: UnitOfMeasure): UnitOfMeasure => {
        const validUnitsOfMeasure = symbol1.symbolCategory === SymbolCategory.Energy ? energySpreadUnitOfMeasures : dairySpreadUnitOfMeasures;
        const product1HasValidUnitOfMeasure = validUnitsOfMeasure.some((x: UnitOfMeasure) => x === symbol1.unitOfMeasure);
        const currentUnitOfMeasureIsValid = validUnitsOfMeasure.some((x: UnitOfMeasure) => x === currentUnitOfMeasure);
        if (product1HasValidUnitOfMeasure) {
            return symbol1.unitOfMeasure!;
        }

        if (currentUnitOfMeasureIsValid) {
            return currentUnitOfMeasure;
        }

        return validUnitsOfMeasure[0];
    };

    getDatesInRange = (startDate: Date, endDate: Date): Date[] => {
        const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

        const days: Date[] = [];
        for (let currentDate = new Date(start); currentDate <= end; currentDate.setDate(currentDate.getDate() + 1)) {
            if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
                days.push(new Date(currentDate));
            }
        }
        return days;
    };

    getCutoffDate = (timeSpan: DemeterFilterTimeSpan, mostRecentDate: Date) => {
        const cutoffDate = new Date(mostRecentDate);

        switch (timeSpan) {
            case DemeterFilterTimeSpan.OneMonth:
                cutoffDate.setMonth(mostRecentDate.getMonth() - 1);
                break;
            case DemeterFilterTimeSpan.SixMonths:
                cutoffDate.setMonth(mostRecentDate.getMonth() - 6);
                break;
            case DemeterFilterTimeSpan.TwoYears:
                cutoffDate.setFullYear(mostRecentDate.getFullYear() - 2);
                break;
            case DemeterFilterTimeSpan.ThreeYears:
                cutoffDate.setFullYear(mostRecentDate.getFullYear() - 3);
                break;
            default:
                cutoffDate.setFullYear(mostRecentDate.getFullYear() - 1);
                break;
        }
        return cutoffDate;
    };

    quantile = (sorted: number[], quantileValue: number) => {
        if (!sorted.length) {
            return undefined;
        }
        const position = (sorted.length - 1) * quantileValue;
        const lowerIndex = Math.floor(position);
        const upperIndex = Math.min(sorted.length - 1, lowerIndex + 1);
        const fraction = position - lowerIndex;
        return lowerIndex === upperIndex ? sorted[lowerIndex] : sorted[lowerIndex] + (sorted[upperIndex] - sorted[lowerIndex]) * fraction;
    };

    buildPeriodCalculation = (values: number[]): ICalendarSpreadPeriodCalculation => {
        const filteredValues = values.filter((value) => value !== null && !Number.isNaN(value));
        if (!filteredValues.length) {
            return {};
        }

        filteredValues.sort((a, b) => a - b);
        const average = filteredValues.reduce((accumulator, value) => accumulator + value, 0) / filteredValues.length;
        const quantilePoints = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
        const deciles = quantilePoints.map((quantilePoint) => this.quantile(filteredValues, quantilePoint)!);

        return { average, deciles };
    };

    buildSpreadPeriodCalculations = (marketPrices: ICalendarSpreadEntriesByDate[]): ICalendarSpreadCalculationResult => {
        const now = new Date();
        const threeMonthsAgo = new Date(now);
        threeMonthsAgo.setMonth(now.getMonth() - 3);
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        const nineMonthsAgo = new Date(now);
        nineMonthsAgo.setMonth(now.getMonth() - 9);
        const twelveMonthsAgo = new Date(now);
        twelveMonthsAgo.setFullYear(now.getFullYear() - 1);

        const allSpreads = marketPrices.filter((x) => x.spread !== null).map((x) => x.spread!);
        const threeMonthSpreads = marketPrices.filter((x) => x.masterAsOfDate >= threeMonthsAgo && x.spread !== null).map((x) => x.spread!);
        const sixMonthSpreads = marketPrices.filter((x) => x.masterAsOfDate >= sixMonthsAgo && x.spread !== null).map((x) => x.spread!);
        const nineMonthSpreads = marketPrices.filter((x) => x.masterAsOfDate >= nineMonthsAgo && x.spread !== null).map((x) => x.spread!);
        const twelveMonthSpreads = marketPrices.filter((x) => x.masterAsOfDate >= twelveMonthsAgo && x.spread !== null).map((x) => x.spread!);

        return {
            contract1: this.buildPeriodCalculation(allSpreads),
            contract2: this.buildPeriodCalculation([]),
            threeMonths: this.buildPeriodCalculation(threeMonthSpreads),
            sixMonths: this.buildPeriodCalculation(sixMonthSpreads),
            nineMonths: this.buildPeriodCalculation(nineMonthSpreads),
            twelveMonths: this.buildPeriodCalculation(twelveMonthSpreads),
        };
    };
}

const calendarSpreadCalculatorService = new CalendarSpreadCalculatorService();
export default calendarSpreadCalculatorService;
